import { Background, Container, ContentArea, HeaderMessage } from '@/components/wm';
import { Check, CircleChevronRight } from 'lucide-react';
import * as React from 'react';
import Plans from './Content/Plans';
import SignupPlans from './Content/SignupPlans';

export function CardFooter ({ children }) {
    return <div className="tw-mt-auto tw-pt-4">{children}</div>;
}

export function FeatureList ({ features, className }) {
    return (
        <ul className="tw-space-y-0.5 tw-clear-float-list">
            {features.map((feature, i) => {
                if (feature.type === 'spacer') {
                    return <li className="tw-relative tw-pl-6 tw-inline-block tw-opacity-0" key={`spacer-${i}`}>NA</li>;
                }
                return (
                    <li className="tw-relative tw-pl-6 tw-inline-block tw-m-0" key={i}>
                        <span className={`tw-relative tw-z-10`}>{feature}</span>
                        <Check aria-hidden="true" className={`tw-w-4 tw-h-4 tw-absolute tw-left-0 tw-top-1/2 tw--translate-y-1/2 tw-z-0 ${className}`}/>
                    </li>
                );
            })}
        </ul>
    );
}

export function ConditionsList ({ conditions, className }) {
    return (
        <ul className="tw-space-y-0.5 tw-clear-float-list">
            {conditions.map((condition, i) => (
                <li className="tw-relative tw-pl-6 tw-inline-block tw-m-0" key={i}>
                    <span className={`tw-relative tw-z-10`}>{condition}</span>
                    <CircleChevronRight aria-hidden="true" className={`tw-w-4 tw-h-4 tw-absolute tw-left-0 tw-top-1/2 tw--translate-y-1/2 tw-z-0 ${className}`}/>
                </li>
            ))}
        </ul>
    );
}

export function ExtraSection ({ extra, className }) {
    if (!extra) {
        return null;
    }
    return (
        <div className="tw-mt-4">
            <p className={`tw-font-bold ${className}`}>{extra.title}</p>
            {extra.text.map((line, i) => (
                <p key={i}>{line}</p>
            ))}
        </div>
    );
}

export function getAllowedTransitions (planSlug) {
    return {
        free: true,
        essentials: ['free'].includes(planSlug),
        standard: ['free', 'essentials'].includes(planSlug),
        advanced: ['free', 'essentials', 'standard'].includes(planSlug),
        premium: ['free', 'essentials', 'standard', 'advanced'].includes(planSlug)
    };
}

export function addSpacersToFeatures (features, plans) {
    const premiumPlan = plans.find(plan => plan.slug === 'premium');
    const maxCount = premiumPlan ? premiumPlan.features.length : features.length;
    const spacersNeeded = maxCount - features.length;
    if (spacersNeeded <= 0) {
        return features;
    }

    return [
        ...features,
        ...Array(spacersNeeded).fill({ type: 'spacer', content: '' })
    ];
}

export default function PlansIndex ({ organization, currentPlan, noAccess }) {
    const plans = React.useMemo(() => [
        {
            key: i18next.t('plans.free'),
            slug: 'free',
            price: '0',
            features: [
                i18next.t('plans.features.data_analysis'),
                i18next.t('plans.features.alarms'),
                i18next.t('plans.features.utilities_costs_invoices'),
                i18next.t('plans.features.free_reports')
            ],
            conditions: [
                i18next.t('plans.conditions.single_user'),
                i18next.t('plans.conditions.data_retention', { years: 3 }),
                i18next.t('plans.conditions.max_1_wise_integrations'),
                i18next.t('plans.conditions.max_sites', { sites: 5 }),
                i18next.t('plans.conditions.max_indicators', { indicators: 1 })
            ],
            extra: {
                title: i18next.t('plans.allows_up_to'),
                text: [i18next.t('plans.features.5_sites'), i18next.t('plans.features.5_indicators')]
            }
        },
        {
            key: i18next.t('plans.essentials'),
            slug: 'essentials',
            price: '5',
            features: [
                i18next.t('plans.features.data_analysis'),
                i18next.t('plans.features.alarms'),
                i18next.t('plans.features.utilities_costs_invoices'),
                i18next.t('plans.features.free_reports')
            ],
            conditions: [
                i18next.t('plans.conditions.unlimited_users'),
                i18next.t('plans.conditions.data_retention', { years: 3 }),
                i18next.t('plans.conditions.max_1_wise_integrations'),
                i18next.t('plans.conditions.contract', { years: 1 }),
                i18next.t('plans.conditions.no_wisebox'),
                i18next.t('plans.conditions.min_sites', { sites: 50 })
            ],
            extra: {
                title: i18next.t('plans.allows_up_to'),
                text: [i18next.t('plans.features.5_sites'), i18next.t('plans.features.5_indicators')]
            }
        },
        {
            key: i18next.t('plans.standard'),
            slug: 'standard',
            price: '45',
            features: [
                i18next.t('plans.features.data_analysis'),
                i18next.t('plans.features.alarms'),
                i18next.t('plans.features.opportunities'),
                i18next.t('plans.features.utilities_costs_invoices'),
                i18next.t('plans.features.objectives'),
                i18next.t('plans.features.utilities_management'),
                i18next.t('plans.features.standard_reports')
            ],
            conditions: [
                i18next.t('plans.conditions.unlimited_users'),
                i18next.t('plans.conditions.data_retention', { years: 3 }),
                i18next.t('plans.conditions.wisebox_4g'),
                i18next.t('plans.conditions.max_wise_integrations', { integrations: 2 }),
                i18next.t('plans.conditions.contract', { years: 2 })
            ]
        },
        {
            key: i18next.t('plans.advanced'),
            slug: 'advanced',
            price: '65',
            features: [
                i18next.t('plans.features.data_analysis'),
                i18next.t('plans.features.alarms'),
                i18next.t('plans.features.opportunities'),
                i18next.t('plans.features.utilities_costs_invoices'),
                i18next.t('plans.features.objectives'),
                i18next.t('plans.features.utilities_management'),
                i18next.t('plans.features.advanced_reports'),
                i18next.t('plans.features.tenants'),
                i18next.t('plans.features.iso_50001_processes')
            ],
            conditions: [
                i18next.t('plans.conditions.unlimited_users'),
                i18next.t('plans.conditions.data_retention', { years: 6 }),
                i18next.t('plans.conditions.wisebox_4g'),
                i18next.t('plans.conditions.advanced_api'),
                i18next.t('plans.conditions.max_wise_integrations', { integrations: 5 }),
                i18next.t('plans.conditions.contract', { years: 2 })
            ]
        },
        {
            key: i18next.t('plans.premium'),
            slug: 'premium',
            price: '85',
            features: [
                i18next.t('plans.features.data_analysis'),
                i18next.t('plans.features.alarms'),
                i18next.t('plans.features.opportunities'),
                i18next.t('plans.features.utilities_costs_invoices'),
                i18next.t('plans.features.objectives'),
                i18next.t('plans.features.utilities_management'),
                i18next.t('plans.features.premium_reports'),
                i18next.t('plans.features.tenants'),
                i18next.t('plans.features.iso_50001_processes'),
                i18next.t('plans.features.technical_support')
            ],
            conditions: [
                i18next.t('plans.conditions.unlimited_users'),
                i18next.t('plans.conditions.perpetual_retention'),
                i18next.t('plans.conditions.wisebox_4g'),
                i18next.t('plans.conditions.premium_api'),
                i18next.t('plans.conditions.max_wise_integrations_unlimited'),
                i18next.t('plans.conditions.contract', { years: 2 })
            ],
            extra: {
                title: i18next.t('plans.special_offer'),
                text: [i18next.t('plans.features.wisestore_integrations'), i18next.t('plans.features.discount_10')]
            }
        }
    ], []);

    if (!organization) {
        return <SignupPlans plans={plans}/>;
    }

    if (noAccess) {
        return (
            <Background>
                <Container variant="full">
                    <ContentArea>
                        <HeaderMessage
                            message={WiseMetering.Permissions.hasRolePermissions('plans', 'update') ? i18next.t('plans.no_access') : i18next.t('plans.no_access_extra')}
                            variant="warning"
                        />
                        <Plans currentPlan={currentPlan} organization={organization} plans={plans}/>
                    </ContentArea>
                </Container>
            </Background>
        );
    }

    return (
        <Background>
            <Container variant="full">
                <ContentArea>
                    <Plans currentPlan={currentPlan} organization={organization} plans={plans}/>
                </ContentArea>
            </Container>
        </Background>
    );
}
